/**
 * Jotai atoms for drawer state management
 * Manages the state of the inventory drawer
 */

import { atom } from 'jotai';

import { InventoryItemCondition, InventoryListingStatus } from '@ekhodealer/ekho-common';

import type { CalloutVariant } from '@ekhodealer/ekho-common/rev';
import type { InventoryDrawerState, InventoryItem } from '../lib/types';

import { MapPolicy, OnlineSales } from '../lib/enums';
import {
	buildDrawerStateFromItem,
	calculateMapPolicyChanges,
	getValidationErrorsForPriceFields,
	inventoryItemCanSave,
} from '../lib/utils';

// Main drawer state atom
export const drawerAtom = atom<InventoryDrawerState>({
	isDrawerOpen: false,
	selectedItemId: null,
	listingStatus: InventoryListingStatus.DRAFT,
	condition: InventoryItemCondition.UNDEFINED,
	onlineSales: OnlineSales.LEAD_GEN,
	dealerRetail: 0,
	advertisedPrice: 0,
	retailPrice: 0,
	mapPolicy: MapPolicy.NOT_SUBJECT_TO_MAP,
	isMapPolicyDropdownHidden: false,
	hasUserMadeChanges: false,
	originalDealerRetail: 0,
	originalAdvertisedPrice: 0,
	originalRetailPrice: 0,
	cosmeticReport: [],
	cosmeticReportSummary: '',
	cosmeticReportEnabled: true,
	originalCosmeticReport: [],
	originalCosmeticReportSummary: '',
	originalCosmeticReportEnabled: true,
});

// Loading state atoms for different operations
export const isPublishingAtom = atom<boolean>(false);
export const isSavingAtom = atom<boolean>(false);
export const isUnpublishingAtom = atom<boolean>(false);

// Open/Close drawer actions
export const openDrawerAtom = atom(null, (get, set, item: InventoryItem) => {
	const drawerState = buildDrawerStateFromItem(item);
	set(drawerAtom, { ...drawerState, isDrawerOpen: true });
});

export const closeDrawerAtom = atom(null, (get, set) => {
	set(drawerAtom, {
		...get(drawerAtom),
		isDrawerOpen: false,
		selectedItemId: null,
	});
	// Reset all loading states when closing drawer
	set(isPublishingAtom, false);
	set(isSavingAtom, false);
	set(isUnpublishingAtom, false);
});

// Publish/Unpublish actions
export const publishInventoryItemAtom = atom(null, (get, set) => {
	set(drawerAtom, {
		...get(drawerAtom),
		listingStatus: InventoryListingStatus.PUBLISHED,
		hasUserMadeChanges: false, // Publishing acts as a save operation
	});
});

export const unpublishInventoryItemAtom = atom(null, (get, set) => {
	set(drawerAtom, {
		...get(drawerAtom),
		listingStatus: InventoryListingStatus.HIDDEN,
		hasUserMadeChanges: false, // Unpublishing acts as a save operation
	});
});

// Online sales updates
export const setOnlineSalesAtom = atom(null, (get, set, onlineSales: OnlineSales) => {
	const currentState = get(drawerAtom);
	const isMapPolicyDropdownHidden =
		currentState.condition === InventoryItemCondition.USED &&
		onlineSales === OnlineSales.ENABLED;

	set(drawerAtom, {
		...currentState,
		onlineSales,
		isMapPolicyDropdownHidden,
		hasUserMadeChanges: true,
	});
});

// Inventory pricing updates
export const setDealerRetailPriceAtom = atom(null, (get, set, value: number) => {
	const currentState = get(drawerAtom);
	set(drawerAtom, {
		...currentState,
		dealerRetail: value,
		hasUserMadeChanges: true,
	});
});

export const setRetailPriceAtom = atom(null, (get, set, value: number) => {
	const currentState = get(drawerAtom);
	const newState = {
		...currentState,
		retailPrice: value,
		hasUserMadeChanges: true,
	};

	// If not subject to MAP policy, retail price should also set advertised price
	if (newState.mapPolicy === MapPolicy.NOT_SUBJECT_TO_MAP && newState.advertisedPrice) {
		newState.retailPrice = newState.advertisedPrice;
	}

	set(drawerAtom, newState);
});

export const setAdvertisedPriceAtom = atom(null, (get, set, value: number) => {
	const currentState = get(drawerAtom);
	const newState = {
		...currentState,
		advertisedPrice: value,
		hasUserMadeChanges: true,
	};

	// If not subject to MAP policy, retail price should sync with advertised price
	if (currentState.mapPolicy === MapPolicy.NOT_SUBJECT_TO_MAP) {
		newState.retailPrice = value;
	}

	set(drawerAtom, newState);
});

export const setMapPolicyAtom = atom(null, (get, set, mapPolicy: MapPolicy) => {
	const currentState = get(drawerAtom);
	const updatedState = calculateMapPolicyChanges(currentState, mapPolicy);
	set(drawerAtom, updatedState);
});

// Derived atom that calculates the callout data based on the listing status
export const calloutAtom = atom((get) => {
	const { listingStatus } = get(drawerAtom);
	switch (listingStatus) {
		case InventoryListingStatus.HIDDEN:
			return {
				variant: 'neutral' as CalloutVariant,
				message: 'This inventory is currently hidden. Publish it again to make it visible.',
			};
		case InventoryListingStatus.INCOMPLETE_DRAFT:
			return {
				variant: 'warning' as CalloutVariant,
				title: 'Details missing',
				message: 'Some fields are incomplete. Please review and update before publishing.',
			};
		case InventoryListingStatus.ARCHIVED:
			return {
				variant: 'neutral' as CalloutVariant,
				message: 'This inventory has been archived and is no longer available for sale.',
			};
		default:
			return null; // No callout needed for other statuses
	}
});

// Derived atom that calculates validationErrors based on current pricing fields
export const validationErrorsAtom = atom((get) => {
	const { dealerRetail, advertisedPrice, retailPrice } = get(drawerAtom);
	return getValidationErrorsForPriceFields(dealerRetail, advertisedPrice, retailPrice);
});

// Derived atom that checks if any blemish entry fails client-side validation.
// Only validates when cosmeticReportEnabled is true — blemish fields are unmounted
// when the toggle is off, so errors cannot be surfaced and must not block saving.
export const cosmeticReportValidationErrorsAtom = atom((get) => {
	const { cosmeticReport, cosmeticReportEnabled } = get(drawerAtom);
	if (!cosmeticReportEnabled) return false;
	return cosmeticReport.some(
		(b) => b.location.length < 3 || b.description.length < 10,
	);
});

// Holds the RHF trigger() function from the CosmeticReport form so that
// handleSave can force-show validation errors without being inside the Form context.
export const cosmeticFormTriggerAtom = atom<(() => Promise<boolean>) | null>(null);
// Derived atom that calculates isPublishDisabled based on current state
export const isPublishDisabledAtom = atom((get) => {
	const { listingStatus, hasUserMadeChanges } = get(drawerAtom);
	const validationErrors = get(validationErrorsAtom);

	// Check for validation errors
	const hasValidationErrors = Object.values(validationErrors).some(
		(error) => error.trim() !== '',
	);

	// Case 1: Item is published and has changes - allow save & publish
	if (listingStatus === InventoryListingStatus.PUBLISHED && hasUserMadeChanges) {
		return false;
	}

	// Case 2: Item is draft/hidden with no validation errors - allow publish
	if (
		listingStatus === InventoryListingStatus.DRAFT ||
		listingStatus === InventoryListingStatus.HIDDEN
	) {
		if (!hasValidationErrors) {
			return false;
		}
	}

	// All other cases - prevent publish
	return true;
});

// Derived atom that calculates isUnpublishDisabled based on listing status
export const isUnpublishDisabledAtom = atom((get) => {
	const { listingStatus } = get(drawerAtom);

	// Can unpublish (enabled = false) when status is PUBLISHED
	// Can't unpublish (disabled = true) for all other statuses
	return listingStatus !== InventoryListingStatus.PUBLISHED;
});

// Save action atom
export const saveInventoryItemAtom = atom(null, (get, set) => {
	set(drawerAtom, {
		...get(drawerAtom),
		hasUserMadeChanges: false, // Saving will reset changes flag
	});
});

// Derived atom that calculates isSaveDisabled based on validation errors and user changes
export const isSaveDisabledAtom = atom((get) => {
	const state = get(drawerAtom);
	const validationErrors = get(validationErrorsAtom);

	const hasClientValidationErrors = Object.values(validationErrors).some((error) => error);
	if (hasClientValidationErrors) return true;

	return !inventoryItemCanSave(state, validationErrors);
});

// Derived atom that determines if the Save button should be shown
export const shouldShowSaveButtonAtom = atom((get) => {
	const { listingStatus } = get(drawerAtom);

	// Hide Save button when item is published
	return listingStatus !== InventoryListingStatus.PUBLISHED;
});

// Derived atom that determines the Publish button label
export const publishButtonLabelAtom = atom((get) => {
	const { listingStatus } = get(drawerAtom);

	// Change label to "Save & publish" when item is published
	return listingStatus === InventoryListingStatus.PUBLISHED ? 'Save & publish' : 'Publish';
});

// ============================================================================
// Navigation State
// ============================================================================

export type InventoryDetailsNavViewId = 'root' | 'inventory-item-detail';
export interface InventoryDetailsNavState {
	/** Current view ID */
	viewId: InventoryDetailsNavViewId;
	/** Navigation history for back functionality */
	history: InventoryDetailsNavViewId[];
}

const defaultInventoryDetailsNavState: InventoryDetailsNavState = {
	viewId: 'root',
	history: [],
};

export const inventoryDetailsNavAtom = atom<InventoryDetailsNavState>(defaultInventoryDetailsNavState);

export const inventoryDetailsNavHandlerAtom = atom((get) => {
	const { viewId, history } = get(inventoryDetailsNavAtom);
	return {
		currentViewId: viewId,
		history,
	};
});